import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { TableCell, TableRow, Typography } from "@mui/material";

import messages from "../../../assets/locale/messages";
import noRequestImg from "../../../assets/images/placeholders/noProductImagePlacholder.svg";
import SearchInput from "../../../components/SearchInput";
import ViewDeleteButton from "../../../components/ViewDeleteButton";
import ViewEditButton from "../../../components/ViewEditButton";
import Table from "../../../components/Table";
import {
  roleDetailsRequest,
  roleDetailsResponse
} from "../../../store/Roles/actions";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import DeleteRole from "../DeleteRole";
import { hasPermission, PERMISSIONS_IDS } from "../../../utils/Roles";
import "./RoleDetails.scss";

const RoleDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const lang = useSelector((state) => state.locale.lang);
  const isLoading = useSelector((state) => state.loader.showLoader);
  const roleDetails = useSelector((state) => state.roles.roleDetails);
  const {
    Name,
    Employees,
    Description,
    EmployeesCount,
    PermissionCount,
    Modules,
    IsSuperAdmin
  } = roleDetails;

  const [searchValue, setSearchValue] = useState("");
  const [deleteRoleModalOpen, setDeleteRoleModalOpen] =
    useState(false);
  const [deletedRoleDetails, setDeletedRoleDetails] = useState({});
  const { roles } = messages[lang];

  const handleDeleteRole = () => {
    setDeletedRoleDetails({
      id,
      name: Name,
      src: ROUTE_PATHS.roleDetails
    });
    setDeleteRoleModalOpen(true);
  };
  const handleCloseModal = () => {
    setDeleteRoleModalOpen(false);
  };

  useEffect(() => {
    dispatch(roleDetailsRequest(id));
    return () => {
      dispatch(roleDetailsResponse({}));
    };
  }, [dispatch, id]);

  const filteredEmployees = Employees?.filter((employee) =>
    employee.Name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const renderPermissionsTableRow = (module) => {
    return (
      <TableRow className=" permission-row " key={`permission-row`}>
        <TableCell className="px-0 fsize-14 font-medium first-cell">
          <p className="p-0 m-0 ">{module?.Name}</p>
          <ul className="permissions-wrapper mt-2 ps-0 d-md-none">
            {module?.Permissions?.map((permission) => (
              <li key={permission.Id} className="text-grey fsize-14">
                {permission.Name}
              </li>
            ))}
          </ul>
        </TableCell>
        <TableCell className="d-none d-md-block">
          <ul className="permissions-wrapper m-0 ps-0">
            {module?.Permissions?.map((permission) => (
              <li key={permission.Id} className="text-grey fsize-14">
                {permission.Name}
              </li>
            ))}
          </ul>
        </TableCell>
      </TableRow>
    );
  };

  const onImageError = (e) => {
    e.target.src = noRequestImg;
  };

  return (
    <div className="role-details-container">
      <div className="page-title pb-4 d-flex justify-content-between align-items-md-center align-items-start">
        <div className="title-count-container d-flex justify-content-center align-items-md-center align-items-start flex-column flex-md-row">
          <div>
            <Typography variant="h5" className="title-color me-1">
              {roles.roleDetails.roleDetails}
            </Typography>
          </div>
        </div>
        {!IsSuperAdmin &&
          roleDetails &&
          !!Object.keys(roleDetails).length && (
            <div className="title-buttons-container d-flex gap-sm-3 gap-1 align-items-center">
              {hasPermission(PERMISSIONS_IDS.roles_edit) && (
                <ViewEditButton
                  onClick={() =>
                    navigate(ROUTE_PATHS.editRole.replace(":id", id))
                  }
                />
              )}
              {hasPermission(PERMISSIONS_IDS.roles_delete) && (
                <ViewDeleteButton onClick={handleDeleteRole} />
              )}
            </div>
          )}
      </div>
      {roleDetails && !!Object.keys(roleDetails).length && (
        <div className="row">
          <div className="col-md-7">
            <div className="w-100 white-bg light-border  border-radius-10 p-30 mb-3">
              <div>
                <Typography
                  variant="h5"
                  className="title-color mb-3 text-break">
                  {Name}
                </Typography>
                <p className="m-0 light-text-color text-break">
                  {Description}
                </p>
              </div>
            </div>
            <div className="w-100 white-bg light-border  border-radius-10 p-30 mb-3 mb-md-0">
              <Typography className="title-color mb-3 fsize-16">
                <span className="me-2 fweight-600">
                  {roles.roleDetails.permissions}
                </span>
                <span className="light-text-color fsize-14">
                  {`(${PermissionCount ? PermissionCount : "0"} ${PermissionCount === 1 ? roles.roleDetails.permission : roles.roleDetails.permissions})`}
                </span>
              </Typography>

              <div className="table-content permissions-table scroll-within">
                <div>
                  <Table
                    rows={
                      !isLoading &&
                      Modules &&
                      Modules?.map((module) =>
                        renderPermissionsTableRow(module)
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-5">
            <div className="w-100 white-bg light-border border-radius-10 p-30">
              <Typography className="title-color mb-3 fsize-16 d-xl-flex ">
                <p className="me-2 fweight-600 mb-2">
                  {roles.roleDetails.roleEmployees}
                </p>
                <p className="light-text-color fsize-14">
                  {`(${(searchValue ? filteredEmployees.length : EmployeesCount) || "0"} ${(searchValue ? filteredEmployees.length : EmployeesCount) === 1 ? roles.roleDetails.employee : roles.roleDetails.employees})`}
                </p>
              </Typography>
              <div className="role-details-search mb-4">
                <SearchInput
                  name="employeeSearch"
                  id="employeeSearch"
                  value={searchValue}
                  inputClass="border-radius-8"
                  inputWrapperClass="search-input-wrapper large-placeholder-search"
                  placeholder={roles.roleDetails.searchPlaceholder}
                  onChange={(value) => {
                    setSearchValue(value);
                  }}
                />
              </div>
              <div className="employees-list scroll-within d-flex flex-column gap-3">
                {!isLoading && filteredEmployees?.length ? (
                  filteredEmployees.map((employee) => (
                    <div
                      className="d-flex gap-3"
                      key={employee.Email}>
                      <figure>
                        <img
                          src={employee.EmployeeImage}
                          alt="employee-image"
                          className=" role-employee-img border-radius-8"
                          onError={onImageError}
                        />
                      </figure>
                      <div>
                        <p className="p-0 m-0 fsize-16 title-color title-color text-break">
                          {employee.Name}
                        </p>
                        <p className="p-0 m-0 fsize-14 text-light-grey text-break">
                          {employee.Email}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="fsize-16 fweight-500 light-text-color text-center">
                    No employees found
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {deleteRoleModalOpen && (
        <DeleteRole
          open={deleteRoleModalOpen}
          closeModal={handleCloseModal}
          roleInfo={deletedRoleDetails}
        />
      )}
    </div>
  );
};

export default RoleDetails;
